import React from 'react';
import { InPlaceEditorComponent, RenderMode, ActionBlur, EditableType, InputType } from '@syncfusion/ej2-react-inplace-editor';
import FacilityStore from '../../stores/FacilityStore';
import ViewerStore from '../../stores/ViewerStore';
import * as Inovx from '@shared';
import { AccordionComponent, AccordionItemDirective, AccordionItemsDirective } from '@syncfusion/ej2-react-navigations';
import viewerActions from '../../actions/ViewerActions';
import AssetActions from '../../actions/AssetActions';
import AssetServices from '../../services/AssetServices';
import ViewerHelpers from '../../helpers/ViewerHelpers';
import { NumericTextBoxModel } from '@syncfusion/ej2-react-inputs';
import { DatePickerModel } from '@syncfusion/ej2-react-calendars';
import { DropDownListModel } from '@syncfusion/ej2-dropdowns';
import ComponentHelpers from '../../helpers/ComponentHelpers';
import { formatExternalTimestamp } from '../../helpers/externalTimestamp';

const { useState, useEffect, useRef } = React;

var ListHelper = Inovx.Shared.Common.ListHelper;
const hasModifyTagsPermission: boolean = ComponentHelpers.hasModifyTagsPermission();

// Boolean attribute values are persisted lower-case, while the editor's drop-down
// items are keyed on these canonical values. Only the item text is localized.
const BOOLEAN_TRUE_ITEM_VALUE = 'True';
const BOOLEAN_FALSE_ITEM_VALUE = 'False';

const getBooleanItemValue = (rawValue: any): string | null => {
    switch (String(rawValue ?? '').trim().toLowerCase()) {
        case 'true': return BOOLEAN_TRUE_ITEM_VALUE;
        case 'false': return BOOLEAN_FALSE_ITEM_VALUE;
        default: return null;
    }
};

const getBooleanItemText = (rawValue: any): string | null => {
    const itemValue = getBooleanItemValue(rawValue);
    if (!itemValue) return null;

    return InovxLoader.t(itemValue === BOOLEAN_TRUE_ITEM_VALUE
        ? 'facility:form.booleanTrue'
        : 'facility:form.booleanFalse');
};

interface AssetInformationProps {
    isVisible: boolean;
    onClose?: () => void
}

interface SelectedAssetInfo {
    id: string,
    name: string,
    assetClassName: string,
    isHierarchyItem: boolean,
    assetID: string | null
}

const AssetInformation = React.forwardRef((props: AssetInformationProps, ref) => {
    const [selectedAssetInfo, setSelectedAssetInfo] = useState<SelectedAssetInfo>(() => prepareSelectedAssetInfo());
    const [attributeInformation, setAttributeInformation] = useState<IAttributeSetValue[]>(() => prepareAttributeInformation());
    const [selectedMeshItem, setSelectedMeshItem] = useState(ViewerStore.getSelectedMeshItem());
    const [assetAttributes, setAssetAttributes] = useState<IAttributeSetValue[]>(() => getStoredAttributeInformation());
    const [isLoadingAttributeData, setIsLoadingAttributeData] = useState<boolean>(true);
    const [hasEditableAttributes, setHasEditableAttributes] = useState<boolean>(false);
    const [enableEditMode, setEnableEditMode] = useState<boolean>(false);
    useEffect(() => {
        if (!props.isVisible) setEnableEditMode(false);
    }, [props.isVisible]);
    const is3DViewerVisible = ViewerStore.is3DViewerVisible();
    const externalLoadVersionRef = useRef(0);
    const externalRequestsRef = useRef<Map<string, AbortController>>(new Map());
    const startedExternalPlaceholdersRef = useRef<Set<string>>(new Set());
    const isInitialAttributeLoadPendingRef = useRef(false);

    const selectedHierarchyItems = ViewerStore.getSelectedHierarchyItems() || [];
    const hasMultipleHierarchyItemsSelected = selectedHierarchyItems.length > 1;

    function prepareSelectedAssetInfo() {
        const selectedHierarchyItems = ViewerStore.getSelectedHierarchyItems();

        if (selectedHierarchyItems?.length === 1) {
            const selectedHierarchyItem = selectedHierarchyItems[0];
            let className = selectedHierarchyItem.ItemClassName;
            const selectedAsset = FacilityStore.getAssetById(selectedHierarchyItem.AssetID);
            if (selectedAsset) {
                const assetClass = FacilityStore.getAssetClasses()?.find(a => a.ID === selectedAsset.AssetClassID);
                if (assetClass) className = assetClass.Name;
            }
            return {
                id: selectedHierarchyItem.ID,
                name: selectedHierarchyItem.Name,
                assetClassName: className,
                isHierarchyItem: true,
                assetID: selectedHierarchyItem.AssetID
            };
        }
        else {
            const selectedAsset = FacilityStore.getAssetById(ViewerStore.getSelectedAsset());
            if (selectedAsset) {
                return {
                    id: selectedAsset.ID,
                    name: selectedAsset.Identity,
                    assetClassName: selectedAsset.AssetClass?.Name,
                    isHierarchyItem: false,
                    assetID: selectedAsset.ID
                };
            }
        }
        return null;
    }

    function prepareAttributeInformation(): IAttributeSetValue[] {
        if (!selectedAssetInfo) return [];

        if (selectedAssetInfo.isHierarchyItem) {
            const selectedHierarchyItems = ViewerStore.getSelectedHierarchyItems();
            if (selectedHierarchyItems?.length === 1 && selectedHierarchyItems[0].InternalAttributes) {
                const selectedHierarchyItem = selectedHierarchyItems[0];
                const grouped: { [key: string]: any[] } = {};
                selectedHierarchyItem.InternalAttributes.forEach((attr: any) => {
                    const setName = attr.SetDefinitionName || 'Default';
                    if (!grouped[setName]) grouped[setName] = [];
                    grouped[setName].push({
                        Name: attr.Name,
                        Value: attr.Value,
                        IsUrl: attr.IsUrl,
                        IsReadOnly: attr.IsReadOnly,
                        DataType: attr.DataType,
                        ID: attr.ID,
                        IsExternal: attr.IsExternal,
                        DiagnosticTooltip: attr.DiagnosticTooltip,
                    });
                });
                return Object.keys(grouped)
                    .map(setName => ({
                        SetDefinitionName: setName,
                        AttributeValues: grouped[setName]
                    }));
            }
            return [];
        } else {
            return getStoredAttributeInformation();
        }
    }

    function getStoredAttributeInformation(): IAttributeSetValue[] {
        const attributes = ViewerStore.getAttributesInformation();
        return Array.isArray(attributes) ? attributes : [];
    }

    const updateViewerState = () => {
        // This check is here to prevent a React warning about setting state on an unmounted component
        //if (!ViewerStore.isPanoViewerVisible())
        //    return;

        const newAssetInfo = updateSelectedAssetInfo();
        if (!newAssetInfo?.isHierarchyItem || newAssetInfo.assetID) {
            setAssetAttributes(getStoredAttributeInformation());
        }
        
        setSelectedMeshItem(ViewerStore.getSelectedMeshItem());
    }
    const updateFacilityState = () => {
        updateSelectedAssetInfo();
    }

    const updateSelectedAssetInfo = () => {
        const selectedAssetInfo = prepareSelectedAssetInfo();

        setSelectedAssetInfo(prevAssetInfo => {
            if (!selectedAssetInfo)
                return null;
            else if (prevAssetInfo?.id == selectedAssetInfo?.id && prevAssetInfo?.name == selectedAssetInfo?.name && prevAssetInfo?.assetClassName == selectedAssetInfo?.assetClassName && prevAssetInfo?.isHierarchyItem == selectedAssetInfo?.isHierarchyItem)
                return prevAssetInfo;
            else
                return { id: selectedAssetInfo?.id, name: selectedAssetInfo?.name, assetClassName: selectedAssetInfo?.assetClassName, isHierarchyItem: selectedAssetInfo?.isHierarchyItem, assetID: selectedAssetInfo?.assetID };
        });

        return selectedAssetInfo;
    }

    useEffect(() => {
        const handleViewerStoreChange = () => {
            updateViewerState();
        };
        const handleFacilityStoreChange = () => {
            updateFacilityState();
        };

        ViewerStore.addChangeListener(handleViewerStoreChange);
        FacilityStore.addChangeListener(handleFacilityStoreChange);

        updateSelectedAssetInfo();

        return () => {
            ViewerStore.removeChangeListener(handleViewerStoreChange);
            FacilityStore.removeChangeListener(handleFacilityStoreChange);
        }
        
    }, [])

    useEffect(() => {
        setEnableEditMode(false);
        resetExternalAttributeLoading();
        isInitialAttributeLoadPendingRef.current = true;

        const selectedHierarchyItems = ViewerStore.getSelectedHierarchyItems() || [];
        const hasMultipleHierarchyItemsSelected = selectedHierarchyItems.length > 1;

        if (hasMultipleHierarchyItemsSelected) {
            isInitialAttributeLoadPendingRef.current = false;
            setIsLoadingAttributeData(false);
            setAttributeInformation([]);
            setTimeout(() => {
                viewerActions.resetAttributesInformation();
            }, 0);
            return;
        }

        if (selectedAssetInfo) {
            if (selectedAssetInfo.isHierarchyItem) {
                if (selectedAssetInfo.assetID) {
                    setIsLoadingAttributeData(true);
                    setTimeout(() => {
                        viewerActions.getAttributesInformation(selectedAssetInfo.assetID);
                    }, 0);
                }
                else {
                    isInitialAttributeLoadPendingRef.current = false;
                    const attributes = prepareAttributeInformation();
                    setAttributeInformation(attributes);
                }
            }
            else {
                setIsLoadingAttributeData(true);
                setTimeout(() => {
                    viewerActions.getAttributesInformation(selectedAssetInfo.assetID);
                }, 0);
            }
        }
        else {
            isInitialAttributeLoadPendingRef.current = false;
            setIsLoadingAttributeData(false);
            setTimeout(() => {
                viewerActions.resetAttributesInformation();
            }, 0);
        }
    }, [selectedAssetInfo])

    useEffect(() => {
        return () => {
            abortExternalAttributeRequests();
        };
    }, [])

    useEffect(() => {
        resetExternalAttributeLoading();
        isInitialAttributeLoadPendingRef.current = false;

        if (selectedAssetInfo?.isHierarchyItem && selectedAssetInfo.assetID) {
            const hierarchyItemAttributes = prepareAttributeInformation();
            const allAttributes = [...(Array.isArray(assetAttributes) ? assetAttributes : []), ...hierarchyItemAttributes];

            setAttributeInformation(allAttributes);
        }
        else{
            setAttributeInformation(Array.isArray(assetAttributes) ? assetAttributes : []);
        }
    }, [assetAttributes])

    useEffect(() => {
        if (isInitialAttributeLoadPendingRef.current || isLoadingAttributeData || !selectedAssetInfo?.assetID || !attributeInformation?.length) return;

        const placeholders = attributeInformation.filter(isExternalLoadingPlaceholder);
        if (!placeholders.length) return;

        const loadVersion = externalLoadVersionRef.current;
        placeholders.forEach(placeholder => {
            const placeholderID = placeholder.ExternalPlaceholderID;
            if (!placeholderID || externalRequestsRef.current.has(placeholderID) || startedExternalPlaceholdersRef.current.has(placeholderID)) return;

            startedExternalPlaceholdersRef.current.add(placeholderID);
            const controller = new AbortController();
            externalRequestsRef.current.set(placeholderID, controller);

            AssetServices.getExternalAssetAttributes(selectedAssetInfo.assetID, placeholderID, controller.signal)
                .then(result => {
                    externalRequestsRef.current.delete(placeholderID);
                    startedExternalPlaceholdersRef.current.delete(placeholderID);
                    if (loadVersion !== externalLoadVersionRef.current) return;

                    setAttributeInformation(current =>
                        replaceExternalPlaceholder(current, placeholderID, result?.length ? result : [createFailedExternalPlaceholder(placeholder)]));
                })
                .catch(error => {
                    externalRequestsRef.current.delete(placeholderID);
                    startedExternalPlaceholdersRef.current.delete(placeholderID);
                    if (isAbortError(error) || loadVersion !== externalLoadVersionRef.current) return;

                    setAttributeInformation(current =>
                        replaceExternalPlaceholder(current, placeholderID, [createFailedExternalPlaceholder(placeholder, getExternalAttributeErrorTooltip(error))]));
                });
        });
    }, [attributeInformation, isLoadingAttributeData, selectedAssetInfo?.assetID])

    useEffect(() => {
        setIsLoadingAttributeData(false);
        addTooltipForTruncatedText();

        const hasEditable = (attributeInformation?.length > 0) ? attributeInformation.some(set => set.AttributeValues.some(attr => !attr.IsReadOnly)) : false;
        setHasEditableAttributes(hasEditable);
    }, [attributeInformation])

    const escapeHtml = (value: string) =>
        value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

    const normalizeTooltipValue = (value: any) =>
        typeof value === 'string' || typeof value === 'number' ? `${value}`.trim() : '';

    const isDiagnosticIdLine = (line: string) =>
        /^Diagnostic\s*ID:/i.test(line) || /^DiagnosticId:/i.test(line);

    const isStatusCodeLine = (line: string) =>
        /^(Status|Status\s*Code|StatusCode|HTTP\s*Status)\s*:?\s*\d{3}\b.*$/i.test(line) ||
        /^HTTP\s+\d{3}\b.*$/i.test(line) ||
        /^\d{3}$/.test(line);

    const extractDiagnosticId = (...values: any[]) => {
        for (const value of values) {
            const text = normalizeTooltipValue(value);
            const diagnosticMatch = text.match(/Diagnostic\s*ID:\s*([^\s]+)/i) || text.match(/DiagnosticId:\s*([^\s]+)/i);
            if (diagnosticMatch?.[1]) return diagnosticMatch[1];
        }

        return '';
    }

    const sanitizeDiagnosticMessage = (value: any) =>
        normalizeTooltipValue(value)
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(line => line && !isDiagnosticIdLine(line) && !isStatusCodeLine(line))
            .join('\n');

    const createDiagnosticTooltip = (message: any, diagnosticID?: any, fallbackMessage?: any) => {
        const normalizedMessage = sanitizeDiagnosticMessage(message) || sanitizeDiagnosticMessage(fallbackMessage);
        const normalizedDiagnosticID = normalizeTooltipValue(diagnosticID);
        return [
            normalizedMessage,
            normalizedDiagnosticID ? `Diagnostic ID: ${normalizedDiagnosticID}` : ''
        ]
            .filter(value => value)
            .filter((value, index, values) => values.indexOf(value) === index)
            .join('\n');
    }

    const getExternalDiagnosticTooltip = (attributeSet: IAttributeSetValue) => {
        if (!attributeSet?.IsExternal) return '';

        const tooltipMessage = sanitizeDiagnosticMessage(attributeSet.DiagnosticTooltip);
        const diagnosticID = extractDiagnosticId(attributeSet.DiagnosticTooltip);

        return createDiagnosticTooltip(tooltipMessage, diagnosticID);
    }

    const acrdnheader = (attributeSet: IAttributeSetValue) => {
        const suffix = attributeSet?.IsExternal && attributeSet.ExternalNoData
            ? InovxLoader.t('facility:externalAttributes.noDataSuffix')
            : '';
        const name = attributeSet.SetDefinitionName || '';
        const diagnosticTitle = getExternalDiagnosticTooltip(attributeSet);
        const diagnostic = diagnosticTitle
            ? `<span class="e-icons e-warning asset-attribute-status-icon" title="${escapeHtml(diagnosticTitle)}" aria-label="${escapeHtml(diagnosticTitle)}"></span>`
            : '';
        const suffixMarkup = suffix
            ? `<span class="asset-attribute-header-suffix">${escapeHtml(suffix)}</span>`
            : '';
        return `<div key='${escapeHtml(name)}' class="d-flex a-ic gap-1 asset-attribute-header"><span class="asset-attribute-header-name">${escapeHtml(name)}</span>${suffixMarkup}${diagnostic}</div>`;
    };

    const abortExternalAttributeRequests = () => {
        externalRequestsRef.current.forEach(controller => controller.abort());
        externalRequestsRef.current.clear();
    }

    const resetExternalAttributeLoading = () => {
        externalLoadVersionRef.current += 1;
        abortExternalAttributeRequests();
        startedExternalPlaceholdersRef.current.clear();
    }

    const isExternalLoadingPlaceholder = (item: IAttributeSetValue) =>
        item?.IsExternal && item.ExternalLoadStatus === "Loading" && !!item.ExternalPlaceholderID;

    const isExternalFailedPlaceholder = (item: IAttributeSetValue) =>
        item?.IsExternal && item.ExternalLoadStatus === "Failed";

    const replaceExternalPlaceholder = (items: IAttributeSetValue[], placeholderID: string, replacement: IAttributeSetValue[]) => {
        if (!Array.isArray(items)) return [];

        const index = items.findIndex(item => item.ExternalPlaceholderID === placeholderID);
        if (index < 0) return items;

        return [
            ...items.slice(0, index),
            ...replacement,
            ...items.slice(index + 1)
        ];
    }

    const createFailedExternalPlaceholder = (placeholder: IAttributeSetValue, diagnosticTooltip?: string): IAttributeSetValue => ({
        ...placeholder,
        ExternalLoadStatus: "Failed",
        DiagnosticTooltip: diagnosticTooltip,
        AttributeValues: []
    });

    const getExternalAttributeErrorTooltip = (error: any) => {
        const responseData = error?.responseData;
        const message = normalizeTooltipValue(responseData?.Message) || InovxLoader.t('facility:externalAttributes.attributeLoadFailure');
        return createDiagnosticTooltip(message, responseData?.DiagnosticId || extractDiagnosticId(error?.message));
    }

    const isAbortError = (error: any) =>
        error?.name === 'AbortError' || error?.isAbort === true;

    const acrdnContent = (attributeSet: IAttributeSetValue) => {
        if (isExternalLoadingPlaceholder(attributeSet)) {
            const title = InovxLoader.t('facility:externalAttributes.attributeLoading');
            return (<div className="asset-table">
                <div className="d-flex h-22">
                    <div className="left-row"><div className="single-line-ellipsis d-flex a-ic h-100p">{InovxLoader.t('facility:externalAttributes.attributeStatus')}</div></div>
                    <div className="right-row">
                        <div className="single-line-ellipsis d-flex a-ic h-100p">
                            <span className="asset-attribute-loading-text" title={title} aria-label={title}>
                                {InovxLoader.t('facility:externalAttributes.attributeLoadingValue')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>);
        }

        if (isExternalFailedPlaceholder(attributeSet)) {
            return <div className="asset-table"></div>;
        }

        const item = ListHelper.sort(attributeSet.AttributeValues || [], "Name");

        const getAttributeValue = (row: any) => {
            if (row.DataType === AttributeDataType.Date) {
                return new Date(row.Value);
            }
            return row.Value;
        }
        const updateAttribute = (row: any, val: any) => {
            if (row.IsReadOnly || row.IsExternal) return;

            let attributeInfo = { AssetIdentityID: selectedAssetInfo.assetID, AttributeDefinitionID: row.ID, Value: val };
            if (val && row.DataType === AttributeDataType.Date) {
                attributeInfo.Value = new Date(val).toLocaleDateString('en-CA');
            }
            AssetActions.updateAssetAttribute(attributeInfo);
        }
        const renderValue = (item: any) => {
            if (!item.Value) return null;

            if (item.IsExternal && item.DataType === AttributeDataType.Date) {
                const timestamp = formatExternalTimestamp(item.Value, InovxLoader.getLocaleData());
                if (timestamp) {
                    return <span
                        className="single-line-ellipsis"
                        data-preserve-tooltip="true"
                        title={timestamp.tooltip}
                    >{timestamp.visible}</span>;
                }
            }

            if (item.DataType === 4) {
                return getBooleanItemText(item.Value) ?? item.Value;
            }else if (item.DataType === 5) {
                return item.Value;
            }else if (item.IsUrl || item.DataType === 3) {
                return <a target="_blank" href={item.Value}>{item.Value}</a>;
            } else {
                return item.Value;
            }
        }
        const renderIndicators = (row: any) => {
            const diagnosticID = extractDiagnosticId(row.DiagnosticTooltip, row.Value);
            const tooltipMessage = sanitizeDiagnosticMessage(row.DiagnosticTooltip);
            const diagnosticTitle = row.DiagnosticTooltip
                ? createDiagnosticTooltip(
                    tooltipMessage || row.Value,
                    diagnosticID,
                    InovxLoader.t('facility:externalAttributes.attributeLoadFailure'))
                : '';

            return <>
                {diagnosticTitle && <span className="e-icons e-warning asset-attribute-status-icon" title={diagnosticTitle} aria-label={diagnosticTitle}></span>}
            </>;
        }
        return (<div>
            <div className="asset-table">
                <div>
                    {item.map((row: any, key: number) => <div className="d-flex h-22" key={`${selectedAssetInfo.id}:${row.ID ?? key}`}>
                        <div className="left-row"><div className="single-line-ellipsis d-flex a-ic h-100p">{row.Name}</div></div>
                        <div className="right-row">
                            {enableEditMode && !row.IsExternal ? <div className="single-line-ellipsis asset-in-place-editor">
                                <InPlaceEditor value={row.Value} dataType={row.DataType} isReadOnly={row.IsReadOnly} onChange={val => updateAttribute(row, val)} />
                            </div> : <div className="single-line-ellipsis d-flex a-ic h-100p gap-1">{renderIndicators(row)}<span className="single-line-ellipsis">{renderValue(row)}</span></div>}
                            
                        </div>
                    </div>)}
                </div>
            </div>
        </div>);
    };

    const addTooltipForTruncatedText = () => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            var elements = document.getElementsByClassName("single-line-ellipsis");

            for (var i = 0; i < elements.length; i++) {
                const div: any = elements[i];
                if (div) {
                    if (div.dataset?.preserveTooltip === "true") {
                        continue;
                    }
                    if (div.scrollWidth > div.clientWidth) {
                        div.title = div.textContent.trim();
                    } else {
                        div.removeAttribute('title');
                    }
                }
            }
        }, 10)

    }
    const changePanelVisibility = () => {
        setEnableEditMode(false);
        viewerActions.changeAssetPanelVisibility(false);
        props.onClose?.();
    }
    return (
        <div className="splitter-content-expand p-2 position-relative">
            {isLoadingAttributeData && <div className="temp-modal-loader asset-modal-loader"><div className="action-in-progress-loader-sm"></div></div>}

            <div className={`bar-header ${selectedAssetInfo ? 'border-bottom' : ''}`}>
                <div className="bar-header-content">
                    {selectedAssetInfo && <>
                        <div className="bar-heading">{selectedAssetInfo?.name}</div>
                        <div className="bar-sub-heading">{selectedAssetInfo?.assetClassName}</div>
                    </>}
                </div>
                <div className="d-flex gap-1">
                    {hasModifyTagsPermission && hasEditableAttributes && <>
                        {(!isLoadingAttributeData && selectedAssetInfo && attributeInformation?.length > 0) && <>
                            {!enableEditMode && <button onClick={() => setEnableEditMode(true)} className="btn no-btn panel-btn edit-btn" type="button" title={InovxLoader.t('facility:action.editAssetAttribute')} />}
                            {enableEditMode && <button onClick={() => setEnableEditMode(false)} className="btn no-btn panel-btn no-edit-btn" type="button" title={InovxLoader.t('facility:action.cancelEdit')} />}
                        </>}
                    </>}
                    
                    <button onClick={changePanelVisibility} className="btn no-btn panel-btn close-btn" type="button" title={InovxLoader.t('facility:action.close')} />
                </div>

            </div>
            {!hasMultipleHierarchyItemsSelected && !isLoadingAttributeData && selectedAssetInfo && attributeInformation?.length > 0 ? <div className="list-panel">
                <AccordionComponent animation={{ expand: { duration: 100, effect: 'SlideDown' }, collapse: { duration: 100, effect: 'SlideUp' } }}>
                    <AccordionItemsDirective>
                        {attributeInformation.map((item: any, key: number) => (
                            <AccordionItemDirective
                                key={key}
                                header={acrdnheader(item)}
                                expanded={true}
                                content={() => acrdnContent(item)}
                            />
                        ))}
                    </AccordionItemsDirective>
                </AccordionComponent>
            </div> : <></>}

            {hasMultipleHierarchyItemsSelected && <div>
                <div className="no-data">
                    <div className="heading">{InovxLoader.t("facility:message.multipleAssetsSelected")}</div>
                    <div className="description">{InovxLoader.t("facility:message.pleaseSelectASingleAsset")}</div>
                </div>
            </div>}

            {!hasMultipleHierarchyItemsSelected && !selectedAssetInfo && selectedMeshItem && <div>
                <div className="no-data">
                    <div className="heading">{InovxLoader.t("facility:message.meshSelected")}</div>
                    <div className="description" style={{ whiteSpace: 'pre-line'}}>{InovxLoader.t("facility:message.pleaseSelectAnAssetForMesh")}</div>
                </div>
            </div>}

            {!hasMultipleHierarchyItemsSelected && !selectedAssetInfo && !selectedMeshItem && <div>
                <div className="no-data">
                    <div className="heading">{InovxLoader.t("facility:message.noAssetSelected")}</div>
                    <div className="description">{InovxLoader.t("facility:message.pleaseSelectAnAsset")}</div>
                </div>
            </div>}
        </div>
    );

});

interface InPlaceEditorProps {
    value: any;
    dataType: number;
    isReadOnly: boolean;
    onChange: (value: any) => void;
}

const InPlaceEditor: React.FC<InPlaceEditorProps> = ({ value, dataType, isReadOnly, onChange }) => {
    const getPlaceholder = (key: string) => InovxLoader.t(`facility:preferences.${key}`);

    const getEditorConfig = (): {
        type: InputType;
        model: any;
        validationRules?: Record<string, any>;
        name?: string;
    } => {
        switch (dataType) {
            case 2:
                return {
                    type: 'Date',
                    model: {
                        placeholder: getPlaceholder('selectDatePlaceholder'),
                        format: 'yyyy-MM-dd'
                    } as DatePickerModel
                };
            case 3:
                return {
                    type: 'Text',
                    name: 'url',
                    model: { placeholder: getPlaceholder('enterUrlPlaceholder'), name: 'url' }
                };
            case 5:
                return {
                    type: 'Numeric',
                    model: {
                        placeholder: getPlaceholder('enterNumberPlaceholder'),
                        validateDecimalOnType: true,
                        showSpinButton: false,
                        enableGroupSeparator: false,
                        format: `###.####################`
                    } as NumericTextBoxModel
                };
            case 4:
                return {
                    type: 'DropDownList',
                    model: {
                        dataSource: [
                            { text: InovxLoader.t('facility:form.booleanTrue'), value: BOOLEAN_TRUE_ITEM_VALUE },
                            { text: InovxLoader.t('facility:form.booleanFalse'), value: BOOLEAN_FALSE_ITEM_VALUE }
                        ],
                        fields: { text: 'text', value: 'value' },
                        placeholder: getPlaceholder('selectBooleanPlaceholder')
                    } as DropDownListModel
                };
            case 1:
            
            default:
                return {
                    type: 'Text',
                    model: { placeholder: getPlaceholder('enterTextPlaceholder') }
                };
        }
    };

    const { type, model, validationRules, name } = getEditorConfig();
    const booleanItemValue = dataType === 4 ? getBooleanItemValue(value) : null;
    const getDisplayValue = () => {
        if (dataType === 4) return booleanItemValue ?? value;
        if (dataType === 5) {
            return value?.toString();
        }
        return value ?? '';
    };

    // InPlaceEditor's default textOption ('Never') renders the bound value verbatim, so a
    // boolean attribute showed the raw "True"/"False" next to a localized drop-down.
    // 'Always' makes it resolve the matching drop-down item's text instead. It is only
    // safe for a value that matches an item: an unmatched value leaves the control stuck
    // on its "loading" placeholder, so anything unrecognized keeps the verbatim render.
    const textOption = booleanItemValue ? 'Always' : 'Never';
    return (
        <InPlaceEditorComponent
            mode={'Popup' as RenderMode}
            type={type}
            name={name}
            value={getDisplayValue()}
            textOption={textOption}
            showButtons={true}
            submitOnEnter={true}
            actionOnBlur={'Submit' as ActionBlur}
            cssClass="inplace-editor"
            model={model}
            emptyText={' '}
            validationRules={validationRules}
            popupSettings={{ model: { width: '300px' } }}
            actionBegin={(args) => {
                if (args.name === 'actionBegin' && args.data.name === 'url') {
                    const value = args.data.value?.trim();
                    const container = document.querySelector('.e-component-group');

                    // Remove old error message if exists
                    const oldError = container?.querySelector('.custom-error');
                    if (oldError) oldError.remove();

                    try {
                        if (value) {
                            new URL(value); // ≡ƒöì Valid URL check
                        }
                    } catch {
                        args.cancel = true;

                        // ≡ƒö╗ Inject error message into the DOM manually
                        const errorMsg = document.createElement('div');
                        errorMsg.textContent = InovxLoader.t('facility:message.enterValidUrl');
                        errorMsg.className = 'custom-error';

                        container?.appendChild(errorMsg);
                    }
                }
            }}
            actionSuccess={(e: any) => {
                let newValue = e.value;
                if (dataType === 5) {
                    newValue = newValue ? Number(e.value) : null;
                }
                if (dataType === 4) newValue = String(e.value).toLowerCase();
                onChange(newValue);
            }}
            disabled={isReadOnly || !hasModifyTagsPermission}
        />
    );
};

export default React.memo(AssetInformation);
